import dayjs, { Dayjs } from 'dayjs';
import { createContext, useContext, ReactNode, useMemo, useState, useCallback, useEffect } from 'react';
import { useGlobalSubscriptions } from 'hooks/Subscriptions/useGlobalSubscriptions';
import useIsViewport from 'hooks/useIsViewport';

type MarketDatesContextType = {
  date: Dayjs;
  startDate: Dayjs;
  dateRange: number;
  onDateChange: (date: Dayjs) => void;
  isLivePreview?: boolean;
};

const MarketDatesContext = createContext<MarketDatesContextType | undefined>(undefined);

export const MarketDatesProvider = ({ children }: { children: ReactNode }) => {
  const { marketLive } = useGlobalSubscriptions();
  const isOpen = !!marketLive?.isOpen;
  const isOpeningTomorrow = !!marketLive?.isOpeningTomorrow;
  const isMobile = useIsViewport();
  const dateRange = isMobile ? 5 : 7;

  const [date, setDate] = useState<Dayjs>(dayjs().add(1, 'day'));
  const [startDate, setStartDate] = useState(dayjs());
  const [isLivePreview, setIsLivePreview] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const validateLivePreview = useCallback(
    (date: Dayjs) => {
      setIsLivePreview(!date.isSame(dayjs(), 'day') && !isOpen);
    },
    [isOpen],
  );

  useEffect(() => {
    if (!isInitialized && marketLive?.isOpen !== undefined) {
      setIsInitialized(true);
      let newDate = dayjs().add(1, 'day');
      if (!isOpen) {
        newDate = isOpeningTomorrow ? dayjs().add(2, 'day') : dayjs().add(8 - dayjs().day(), 'days');
      }
      setDate(newDate);
    }
  }, [isOpen, isOpeningTomorrow, isInitialized, marketLive?.isOpen]);

  const handleDateChange = useCallback(
    (newDate: Dayjs) => {
      setDate(newDate);
      setIsLivePreview(!newDate.isSame(dayjs(), 'day') && !isOpen);

      const startDateDiff = newDate.diff(startDate, 'days');
      const newStartDate =
        startDateDiff < 0 ? startDate.subtract(1, 'day') : startDateDiff > dateRange ? startDate.add(1, 'day') : null;

      if (newStartDate) {
        setStartDate(newStartDate);
      } else {
        setDate(newDate);
        validateLivePreview(newDate);
      }
    },
    [isOpen, startDate, dateRange, validateLivePreview],
  );

  const value = useMemo(
    () => ({
      date,
      startDate,
      dateRange,
      onDateChange: handleDateChange,
      isLivePreview,
    }),
    [date, startDate, isLivePreview, handleDateChange, dateRange],
  );

  return <MarketDatesContext.Provider value={value}>{children}</MarketDatesContext.Provider>;
};
export const useMarketDates = (): MarketDatesContextType => {
  const context = useContext(MarketDatesContext);
  if (!context) {
    throw new Error('useMarketDates must be used within a MarketDatesProvider');
  }
  return context;
};
